import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Users, Plus, Edit, UserX, Trash2, Mail, Shield, Send, Clock, RefreshCw, Link2 } from 'lucide-react';
import { toast } from 'sonner';

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'branch_manager',
    branch_ids: [],
    region_ids: []
  });

  // Invite modal state — uses the new /api/users/invite endpoint
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'branch_manager',
    branch_ids: [],
    area_ids: [],
  });

  // Delete-confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchBranches();
    fetchRegions();
    fetchAreas();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/users`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API}/branches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${API}/regions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setRegions(data);
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchAreas = async () => {
    try {
      const res = await fetch(`${API}/areas`, { credentials: 'include' });
      if (res.ok) setAreas(await res.json());
    } catch (e) { console.error('Error fetching areas:', e); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (inviting) return;
    const role = inviteForm.role;
    if (role === 'branch_manager' && (!inviteForm.branch_ids || inviteForm.branch_ids.length === 0)) {
      toast.error('At least one branch is required for Branch Manager');
      return;
    }
    if (role === 'area_manager' && (!inviteForm.area_ids || inviteForm.area_ids.length === 0)) {
      toast.error('At least one area is required for Area Manager');
      return;
    }
    setInviting(true);
    try {
      const body = { email: inviteForm.email.trim(), name: inviteForm.name.trim(), role };
      if (role === 'branch_manager') body.branch_ids = inviteForm.branch_ids;
      if (role === 'area_manager') body.area_ids = inviteForm.area_ids;
      const res = await fetch(`${API}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Invitation sent');
        setInviteOpen(false);
        setInviteForm({ email: '', name: '', role: 'branch_manager', branch_ids: [], area_ids: [] });
        fetchUsers();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send invitation');
      }
    } catch { toast.error('Failed to send invitation'); }
    finally { setInviting(false); }
  };

  const handleResendInvite = async (userData) => {
    try {
      const role = userData.role;
      const body = { email: userData.email, name: userData.name, role };
      if (role === 'branch_manager') {
        body.branch_ids = (userData.branch_ids && userData.branch_ids.length)
          ? userData.branch_ids
          : (userData.branch_id ? [userData.branch_id] : []);
      }
      if (role === 'area_manager' || role === 'regional_manager') body.area_ids = userData.area_ids || [];
      // The backend rejects existing emails, so first soft-remove the prior pending user.
      await fetch(`${API}/users/${userData.user_id}`, { method: 'DELETE', credentials: 'include' });
      const res = await fetch(`${API}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Invitation resent');
        fetchUsers();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to resend invitation');
      }
    } catch { toast.error('Failed to resend invitation'); }
  };

  // Generate a fresh invite link and copy it to the clipboard. Provides an
  // out-of-band delivery channel (Slack / SMS / etc.) for clients whose
  // corporate mail security strips our invite emails.
  const handleCopyInviteLink = async (userData) => {
    try {
      const res = await fetch(`${API}/users/${userData.user_id}/invite-link`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate invite link');
        return;
      }
      const data = await res.json();
      const url = data.invite_url || '';
      if (!url) {
        toast.error('No invite URL returned');
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Fallback for browsers without async clipboard API.
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch { /* ignore */ }
        document.body.removeChild(ta);
      }
      toast.success('Link copied!', { duration: 2000 });
    } catch {
      toast.error('Failed to generate invite link');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/users/${deleteTarget.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('User deleted');
        setUsers(prev => prev.filter(u => u.user_id !== deleteTarget.user_id));
        setDeleteTarget(null);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete user');
      }
    } catch { toast.error('Failed to delete user'); }
    finally { setDeleting(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // The dialog now only opens for editing existing users — adding is done
    // exclusively via the Invite User flow.
    if (!isEditing || !selectedUser?.user_id) return;
    try {
      const response = await fetch(`${API}/users/${selectedUser.user_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('User updated successfully');
        setDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Failed to save user');
    }
  };

  const handleEdit = (userData) => {
    setSelectedUser(userData);
    setFormData({
      email: userData.email,
      name: userData.name,
      role: userData.role,
      branch_ids: userData.branch_ids || [],
      region_ids: userData.region_ids || []
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      const response = await fetch(`${API}/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('User deactivated');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to deactivate user');
      }
    } catch (error) {
      console.error('Error deactivating user:', error);
      toast.error('Failed to deactivate user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'branch_manager',
      branch_ids: [],
      region_ids: []
    });
    setIsEditing(false);
    setSelectedUser(null);
  };

  const getRoleBadge = (role) => {
    const badges = {
      tenant_admin: { label: 'Admin', color: 'bg-[#F1D511] text-[#0A0F1B]' },
      hq_staff: { label: 'HQ', color: 'bg-[#94A3B8] text-white' },
      regional_manager: { label: 'Regional', color: 'bg-[#5BC4BF] text-white' },
      area_manager: { label: 'Area', color: 'bg-[#5BC4BF] text-white' },
      branch_manager: { label: 'Branch', color: 'bg-[#4EBC87] text-white' }
    };
    return badges[role] || badges.branch_manager;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Montserrat']" data-testid="users-title">
              User Management
            </h1>
            <p className="text-[#64748B] mt-1">
              Manage team members and their access permissions
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-['Montserrat'] text-[#0F172A]">
                  {isEditing ? 'Edit User' : 'Add New User'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    disabled={isEditing}
                    data-testid="user-email-input"
                  />
                  {!isEditing && (
                    <p className="text-xs text-[#64748B]">
                      User will log in via Google with this email
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full name"
                    required
                    data-testid="user-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant_admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#F1D511]" />
                          Tenant Admin
                        </div>
                      </SelectItem>
                      <SelectItem value="hq_staff">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#94A3B8]" />
                          HQ Staff
                        </div>
                      </SelectItem>
                      <SelectItem value="regional_manager">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#5BC4BF]" />
                          Regional Manager
                        </div>
                      </SelectItem>
                      <SelectItem value="area_manager">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#5BC4BF]" />
                          Area Manager
                        </div>
                      </SelectItem>
                      <SelectItem value="branch_manager">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#4EBC87]" />
                          Branch Manager
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.role === 'regional_manager' && (
                  <div className="space-y-2">
                    <Label>Assigned Regions</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                      {regions.length === 0 ? (
                        <p className="text-sm text-[#64748B] col-span-2">No regions available</p>
                      ) : (
                        regions.map((region) => (
                          <label 
                            key={region.region_id} 
                            className="flex items-center gap-2 text-sm cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.region_ids.includes(region.region_id)}
                              onChange={(e) => {
                                const newIds = e.target.checked
                                  ? [...formData.region_ids, region.region_id]
                                  : formData.region_ids.filter(id => id !== region.region_id);
                                setFormData({ ...formData, region_ids: newIds });
                              }}
                              className="rounded"
                            />
                            {region.name}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {formData.role === 'branch_manager' && (
                  <div className="space-y-2">
                    <Label>Assigned Branches</Label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                      {branches.length === 0 ? (
                        <p className="text-sm text-[#64748B] col-span-2">No branches available</p>
                      ) : (
                        branches.map((branch) => (
                          <label 
                            key={branch.branch_id} 
                            className="flex items-center gap-2 text-sm cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.branch_ids.includes(branch.branch_id)}
                              onChange={(e) => {
                                const newIds = e.target.checked
                                  ? [...formData.branch_ids, branch.branch_id]
                                  : formData.branch_ids.filter(id => id !== branch.branch_id);
                                setFormData({ ...formData, branch_ids: newIds });
                              }}
                              className="rounded"
                            />
                            {branch.name}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#1A3E6F] hover:bg-[#122c50]"
                    data-testid="save-user-btn"
                  >
                    {isEditing ? 'Save Changes' : 'Create User'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#1A3E6F] hover:bg-[#122c50] text-white font-['Montserrat'] font-semibold"
                data-testid="invite-user-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]" data-testid="invite-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Montserrat'] text-[#0F172A]">Invite User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleInvite} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Name *</Label>
                  <Input
                    id="invite-name"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="Full name"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email *</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="user@example.com"
                    required
                    data-testid="invite-email-input"
                  />
                  <p className="text-xs text-[#64748B]">An invitation email with a set-password link will be sent. Link expires in 72 hours.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-role">Role *</Label>
                  <Select
                    value={inviteForm.role}
                    onValueChange={(v) => setInviteForm({ ...inviteForm, role: v, branch_id: '', area_ids: [] })}
                  >
                    <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
                      <SelectItem value="hq_staff">HQ Staff</SelectItem>
                      <SelectItem value="area_manager">Area Manager</SelectItem>
                      <SelectItem value="branch_manager">Branch Manager</SelectItem>
                      <SelectItem value="client">Client (read-only reports)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {inviteForm.role === 'area_manager' && (
                  <div className="space-y-2">
                    <Label>Assigned Areas *</Label>
                    <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto p-2 border rounded-lg" data-testid="invite-areas-multiselect">
                      {areas.length === 0 ? (
                        <p className="text-sm text-[#64748B]">No areas yet — create one in Branch Management.</p>
                      ) : areas.map((a) => (
                        <label key={a.area_id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inviteForm.area_ids.includes(a.area_id)}
                            onChange={(e) => {
                              const ids = e.target.checked
                                ? [...inviteForm.area_ids, a.area_id]
                                : inviteForm.area_ids.filter(id => id !== a.area_id);
                              setInviteForm({ ...inviteForm, area_ids: ids });
                            }}
                            className="rounded"
                          />
                          {a.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {inviteForm.role === 'branch_manager' && (
                  <div className="space-y-2">
                    <Label>Assigned Branches *</Label>
                    <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto p-2 border rounded-lg" data-testid="invite-branches-multiselect">
                      {branches.length === 0 ? (
                        <p className="text-sm text-[#64748B]">No branches yet — create one first in Branch Management.</p>
                      ) : branches.map((b) => (
                        <label key={b.branch_id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={inviteForm.branch_ids.includes(b.branch_id)}
                            onChange={(e) => {
                              const ids = e.target.checked
                                ? [...inviteForm.branch_ids, b.branch_id]
                                : inviteForm.branch_ids.filter(id => id !== b.branch_id);
                              setInviteForm({ ...inviteForm, branch_ids: ids });
                            }}
                            className="rounded"
                          />
                          {b.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} disabled={inviting}>Cancel</Button>
                  <Button
                    type="submit"
                    disabled={inviting}
                    className="bg-[#1A3E6F] hover:bg-[#122c50]"
                    data-testid="send-invite-btn"
                  >
                    <Send className="w-4 h-4 mr-2" /> {inviting ? 'Sending…' : 'Send Invite'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Users Table */}
        <Card className="border-[#E2E8F0] shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="loading-spinner mx-auto mb-4"></div>
                <p className="text-[#64748B]">Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-[#B4B4B4] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0F172A] font-['Montserrat']">No team members yet</h3>
                <p className="text-[#64748B] mt-1 mb-4">Add users to help manage your branches</p>
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="bg-[#1A3E6F] hover:bg-[#122c50]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-['Montserrat']">User</TableHead>
                      <TableHead className="font-['Montserrat']">Role</TableHead>
                      <TableHead className="font-['Montserrat']">Assigned To</TableHead>
                      <TableHead className="font-['Montserrat']">Status</TableHead>
                      <TableHead className="font-['Montserrat']">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((userData) => {
                      const roleBadge = getRoleBadge(userData.role);
                      const isCurrentUser = userData.user_id === user?.user_id;
                      
                      return (
                        <TableRow key={userData.user_id} data-testid={`user-row-${userData.user_id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarImage src={userData.picture} />
                                <AvatarFallback className="bg-[#1A3E6F] text-white">
                                  {userData.name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-[#0F172A]">
                                  {userData.name}
                                  {isCurrentUser && (
                                    <span className="ml-2 text-xs text-[#64748B]">(You)</span>
                                  )}
                                </p>
                                <p className="text-sm text-[#64748B] flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {userData.email}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${roleBadge.color}`}>
                              {roleBadge.label}
                            </span>
                          </TableCell>
                          <TableCell>
                            {userData.role === 'tenant_admin' || userData.role === 'hq_staff' ? (
                              <span className="text-[#64748B]">All locations</span>
                            ) : userData.role === 'regional_manager' ? (
                              <span className="text-sm">
                                {userData.region_ids?.length || 0} region(s)
                              </span>
                            ) : userData.role === 'area_manager' ? (
                              <span className="text-sm">
                                {userData.area_ids?.length || 0} area(s)
                              </span>
                            ) : (
                              <span className="text-sm">
                                {userData.branch_ids?.length
                                  ? `${userData.branch_ids.length} branch(es)`
                                  : (userData.branch_id ? '1 branch' : '0 branch(es)')}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {userData.status === 'pending' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[#FBBF24] text-[#0A0F1B]" data-testid={`status-pending-${userData.user_id}`}>
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            ) : (
                              <span className={`badge ${userData.is_active ? 'badge-active' : 'badge-expired'}`}>
                                {userData.is_active ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {userData.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResendInvite(userData)}
                                  className="text-[#64748B] hover:text-[#1A3E6F]"
                                  data-testid={`resend-invite-${userData.user_id}`}
                                  title="Resend invitation"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </Button>
                              )}
                              {userData.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCopyInviteLink(userData)}
                                  className="text-[#64748B] hover:text-[#1A3E6F]"
                                  data-testid={`copy-invite-link-${userData.user_id}`}
                                  title="Copy invite link to clipboard"
                                >
                                  <Link2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(userData)}
                                className="text-[#64748B] hover:text-[#1A3E6F]"
                                data-testid={`edit-user-${userData.user_id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {!isCurrentUser && userData.is_active && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeactivate(userData.user_id)}
                                  className="text-[#64748B] hover:text-[#D62B27]"
                                  data-testid={`deactivate-user-${userData.user_id}`}
                                >
                                  <UserX className="w-4 h-4" />
                                </Button>
                              )}
                              {(user?.role === 'platform_admin' || user?.role === 'tenant_admin') &&
                                !isCurrentUser &&
                                userData.role !== 'platform_admin' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteTarget(userData)}
                                  className="text-[#64748B] hover:text-[#DC2626]"
                                  data-testid={`delete-user-${userData.user_id}`}
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent data-testid="delete-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Montserrat']">Delete User</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#64748B]">
              Delete this user? This cannot be undone.
            </p>
            {deleteTarget && (
              <div className="text-sm text-[#0F172A] bg-[#F8FAFC] border border-[#F1F5F9] rounded-lg px-3 py-2">
                <p className="font-medium">{deleteTarget.name}</p>
                <p className="text-[#64748B]">{deleteTarget.email}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="delete-user-cancel-btn">Cancel</Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="delete-user-confirm-btn"
              >
                {deleting ? 'Deleting…' : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
